import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import { useHackathon } from "../contexts/HackathonContext";
import { PageHeader } from "../components/PageHeader";
import { Avatar } from "../components/Avatar";
import type { School } from "../types/team";
type Filter = "all" | "students" | "mentors";
interface Person {
  id: string;
  name: string;
  photo: string;
  tone: School | "mentor";
  kind: "Student" | "Mentor";
  primary: string;
  secondary: string;
  groups: number[];
  joined: boolean;
}
const filters: {
  id: Filter;
  label: string;
}[] = [{
  id: "all",
  label: "Everyone"
}, {
  id: "students",
  label: "Students"
}, {
  id: "mentors",
  label: "Mentors"
}];
export function People() {
  const {
    state
  } = useHackathon();
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const people = useMemo<Person[]>(() => {
    const s: Person[] = state.students.map(st => ({
      id: st.id,
      name: st.name,
      photo: st.photo,
      tone: st.school,
      kind: "Student",
      primary: st.school === "link" ? "Link School of Business · Brazil" : "Mahindra University · India",
      secondary: [st.role, st.major].filter(Boolean).join(" · "),
      groups: st.groupId ? [st.groupId] : [],
      joined: !!st.joinedAt
    }));
    const m: Person[] = state.mentors.map(mt => ({
      id: mt.id,
      name: mt.name,
      photo: mt.photo,
      tone: "mentor",
      kind: "Mentor",
      primary: mt.organization || "Mentor",
      secondary: mt.expertise,
      groups: mt.groupIds,
      joined: !!mt.joinedAt
    }));
    return [...s, ...m];
  }, [state.students, state.mentors]);
  const q = query.trim().toLowerCase();
  const visible = people.filter(p => (filter === "all" || (filter === "students" ? p.kind === "Student" : p.kind === "Mentor")) && (!q || `${p.name} ${p.primary} ${p.secondary}`.toLowerCase().includes(q)));
  return <main className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-12" data-id="element-915" data-mp="zzl7br93v33u">
      <PageHeader title="People" description={`Every student and mentor in the program · ${state.students.length} students, ${state.mentors.length} mentors.`} data-id="element-916" data-mp="14yxphyp17m03" />

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-917" data-mp="q41bt5yge6fm">
        <div role="tablist" aria-label="Filter people" className="inline-flex rounded-xl bg-ink/5 p-1" data-id="element-918" data-mp="v2unqty3dpdo">
          {filters.map(f => <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} onClick={() => setFilter(f.id)} className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-semibold transition-colors duration-150 ${filter === f.id ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink"}`} data-id="element-919" data-mp="m0le06ane01c">
              {f.label}
            </button>)}
        </div>
        <label className="relative block sm:w-72" data-id="element-920" data-mp="3fseoibpwdch7">
          <span className="sr-only" data-id="element-921" data-mp="3fv13gwf4au9g">Search people</span>
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-922" data-mp="3av4wqpf1coq7" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name, school, skill" className="w-full rounded-xl border border-line bg-surface py-2.5 pl-9 pr-3 text-sm outline-none focus:border-ink" data-id="element-923" data-mp="3orrxl3a3zz1e" />
        </label>
      </div>

      {visible.length === 0 ? <p className="mt-8 rounded-xl border border-dashed border-line px-6 py-12 text-center text-sm text-muted" data-id="element-924" data-mp="bdwx81aj2k7t">
          {filter === "mentors" && state.mentors.length === 0 ? "Mentors will be announced soon." : "No one matches that search."}
        </p> : <ul className="mt-6 grid gap-x-8 sm:grid-cols-2" data-id="element-925" data-mp="geuf0crmlc8s">
          {visible.map(p => <li key={p.id} className="border-b border-line" data-id="element-926" data-mp="10gp68rc68ry1">
              <Link to={`/organizer/people/${p.id}`} className="group flex items-center gap-4 py-4" data-id="element-927" data-mp="10ecsyrhcz5rb">
                <Avatar name={p.name} school={p.tone} photo={p.photo} size="lg" data-id="element-928" data-mp="14feksn8y4fj9" />
                <div className="min-w-0 flex-1" data-id="element-929" data-mp="19998s2vl3go5">
                  <p className="truncate text-[15px] font-semibold group-hover:underline" data-id="element-930" data-mp="1e83v3esrfnd0">{p.name}</p>
                  <p className="truncate text-sm text-muted" data-id="element-931" data-mp="hi37vk81hext">{p.primary}</p>
                  {p.secondary && <p className="truncate text-sm text-muted" data-id="element-932" data-mp="lidd2uo0c1bm">{p.secondary}</p>}
                </div>
                <div className="shrink-0 text-right" data-id="element-933" data-mp="qhghqe02s5j2">
                  <p className={`text-xs font-semibold ${p.kind === "Mentor" ? "text-navy" : "text-muted"}`} data-id="element-934" data-mp="qk2oibf56h08">{p.kind}</p>
                  <p className="text-xs text-muted" data-id="element-935" data-mp="21x54b49f32jc">
                    {p.groups.length ? p.groups.map(g => `G${g}`).join(" · ") : "No group"}
                  </p>
                  {!p.joined && <p className="text-xs text-saffron-ink" data-id="element-936" data-mp="26ycawzztpvof">Profile pending</p>}
                </div>
              </Link>
            </li>)}
        </ul>}
    </main>;
}